import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';
import { toast } from 'react-toastify';
import { LocalizedLink } from '../components/LocalizedLink';
import NonIndexableHelmet from '../components/NonIndexableHelmet';
import { getAuthMeta } from '../data/nonIndexableMeta';
import { useLocale } from '../contexts/LocaleContext';
import { trackRegistrationSuccess } from '../utils/matomo';

const RegisterPage = () => {
  const { t } = useTranslation('auth');
  const { locale } = useLocale();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    termsAccepted: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [formToken, setFormToken] = useState('');
  const [website, setWebsite] = useState('');
  const formTokenFetchedAt = useRef(0);

  useEffect(() => {
    authAPI.getFormToken()
      .then((res) => {
        setFormToken(res.data.token);
        formTokenFetchedAt.current = Date.now();
      })
      .catch((err) => {
        console.error('Failed to fetch form token:', err);
      });
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim() || formData.name.length < 2) {
      newErrors.name = t('register.errName');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('register.errEmailReq');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('register.errEmailFmt');
    }

    if (!formData.password) {
      newErrors.password = t('register.errPwReq');
    } else if (formData.password.length < 8) {
      newErrors.password = t('register.errPwLen');
    }

    if (!formData.termsAccepted) {
      newErrors.termsAccepted = t('register.errTerms');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!formToken) {
      toast.error(t('register.errFormToken'));
      return;
    }

    setLoading(true);
    setSuccess(false);

    try {
      await authAPI.register(
        formData.name,
        formData.email,
        formData.password,
        formData.termsAccepted,
        locale,
        formToken,
        website
      );
      trackRegistrationSuccess();
      setSuccess(true);
      toast.success(t('register.toastOk'));
    } catch (error) {
      console.error('Error during registration:', error);
      const errorMessage = error.response?.data?.message || 'Error';
      const errorCode = error.response?.data?.code;

      if (errorCode === 'EMAIL_ALREADY_EXISTS') {
        setErrors({ email: t('register.errExists') });
        toast.error(t('register.toastExists'));
      } else if (errorCode === 'EMAIL_INVALID') {
        setErrors({ email: t('register.errEmailFmt') });
        toast.error(t('register.errEmailFmt'));
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-gradient">
      <NonIndexableHelmet
        title={locale === 'en' ? 'Sign up | Assistance Video' : getAuthMeta('/register').title}
        description={
          locale === 'en'
            ? 'Create your Assistance Video account to start remote video support: three free sessions and browser links with no app install for customers.'
            : getAuthMeta('/register').description
        }
      />

      <Container>
        <Row className="justify-content-center align-items-center auth-page-shell">
          <Col xs={12} lg={6} xl={5}>
            <Card className="shadow-lg border-0 rounded-lg">
              <Card.Body className="p-4 p-md-5">
                <div className="text-center mb-4">
                  <div style={{ fontSize: '3rem', color: '#0066FF' }}>
                    <i className="bi bi-camera-video-fill" />
                  </div>
                  <h2 className="fw-bold mt-3">Assistance Video</h2>
                  <p className="text-muted">{t('register.subtitle')}</p>
                </div>

                {success ? (
                  <Alert variant="success">
                    <Alert.Heading>
                      <i className="bi bi-check-circle-fill me-2" />
                      {t('register.successTitle')}
                    </Alert.Heading>
                    <p className="mb-0">
                      {t('register.successLead')}
                      <strong>{formData.email}</strong>
                      {t('register.successTrail')}
                    </p>
                    <hr />
                    <p className="mb-0 small">
                      <i className="bi bi-info-circle me-2" />
                      {t('register.valid24')}
                    </p>
                  </Alert>
                ) : (
                  <>
                    <Alert variant="info" className="border-0 mb-4">
                      <i className="bi bi-gift me-2" />
                      <strong>{t('register.giftTitle')}</strong>
                      <p className="mb-0 mt-2 small">{t('register.giftBody')}</p>
                    </Alert>

                    <Form onSubmit={handleSubmit}>
                      <div className="hp-field" aria-hidden="true">
                        <label htmlFor="website">Website</label>
                        <input
                          type="text"
                          id="website"
                          name="website"
                          value={website}
                          onChange={(e) => setWebsite(e.target.value)}
                          tabIndex={-1}
                          autoComplete="off"
                        />
                      </div>

                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">
                          <i className="bi bi-person me-2" />
                          {t('register.firstName')}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          placeholder={t('register.firstNamePh')}
                          value={formData.name}
                          onChange={handleChange}
                          isInvalid={!!errors.name}
                          disabled={loading}
                          required
                        />
                        <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">
                          <i className="bi bi-envelope me-2" />
                          {t('register.email')}
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          placeholder={t('login.emailPh')}
                          value={formData.email}
                          onChange={handleChange}
                          isInvalid={!!errors.email}
                          disabled={loading}
                          required
                        />
                        <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">
                          <i className="bi bi-lock me-2" />
                          {t('register.password')}
                        </Form.Label>
                        <div className="position-relative">
                          <Form.Control
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            placeholder={t('register.passwordPh')}
                            value={formData.password}
                            onChange={handleChange}
                            isInvalid={!!errors.password}
                            disabled={loading}
                            required
                          />
                          <Button
                            variant="link"
                            className="position-absolute end-0 top-0 text-muted"
                            style={{ zIndex: 10, textDecoration: 'none' }}
                            onClick={() => setShowPassword(!showPassword)}
                            tabIndex={-1}
                          >
                            <i className={`bi bi-eye${showPassword ? '-slash' : ''}`} />
                          </Button>
                          <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                        </div>
                        <Form.Text className="text-muted">{t('register.passwordHint')}</Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Check
                          type="checkbox"
                          id="termsAccepted"
                          name="termsAccepted"
                          checked={formData.termsAccepted}
                          onChange={handleChange}
                          isInvalid={!!errors.termsAccepted}
                          disabled={loading}
                          label={
                            <span>
                              <Trans
                                ns="auth"
                                i18nKey="register.terms"
                                components={{
                                  cgu: <LocalizedLink to="/legal/cgu/" target="_blank" className="text-decoration-none" />,
                                  cgv: <LocalizedLink to="/legal/cgv/" target="_blank" className="text-decoration-none" />,
                                }}
                              />
                            </span>
                          }
                          required
                          feedback={errors.termsAccepted}
                          feedbackType="invalid"
                        />
                      </Form.Group>

                      <Button variant="primary" type="submit" size="lg" className="w-100 fw-semibold" disabled={loading}>
                        {loading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            {t('register.creating')}
                          </>
                        ) : (
                          <>
                            <i className="bi bi-person-plus-fill me-2" />
                            {t('register.submit')}
                          </>
                        )}
                      </Button>
                    </Form>

                    <div className="text-center mt-4">
                      <p className="text-muted mb-0">
                        {t('register.haveAccount')}{' '}
                        <LocalizedLink to="/login/" className="text-decoration-none fw-semibold">
                          {t('register.signIn')}
                        </LocalizedLink>
                      </p>
                    </div>
                  </>
                )}

                <hr className="my-4" />

                <div className="text-center">
                  <LocalizedLink to="/" className="text-decoration-none">
                    <i className="bi bi-arrow-left me-2" />
                    {t('login.home')}
                  </LocalizedLink>
                </div>
              </Card.Body>
            </Card>

            <div className="text-center mt-3 mt-md-4 text-white">
              <p className="small">
                <i className="bi bi-shield-check me-2" />
                {t('register.footer')}
              </p>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default RegisterPage;
